// House illustration recipes — a copy of
// `app/supabase/functions/_shared/illustrationRecipe.ts` (2026-09-16) so the
// page composes the same prompt the work order does. If the recipe changes,
// change both files.
//
// ONE house style; the word is "illustration". The kinds are named
// DRAWN / PHOTOGRAPH / CLOSEUP and `neutralize()` rewrites the art sense of
// the retired style-track words out of any caption at compose time. Hair and
// nail vocabulary that happens to contain one of them is left alone.

export type IllustrationKind = 'DRAWN' | 'PHOTOGRAPH' | 'CLOSEUP';

export const DRAWN_RECIPE =
  `A 1930s cosmetology-academy textbook illustration that was originally drawn in BLACK AND WHITE and DIGITALLY COLORIZED afterward. Begin from a pure grayscale espresso-ink line-and-shade drawing on aged parchment paper (#F5EFE6), then add color as a digital colorization pass: soft, gently translucent, slightly muted tints laid over the grayscale so the underlying black-and-white linework and shading still read through. Colorize with the brand palette kept antique and desaturated — moss-green (#5E6B55), sage (#8C9888), clay (#C9A58E), dusty-blue (#6C89AB) — never glossy, saturated, or neon. Labels in clean UPPERCASE monospace caps with thin hairline leader lines; section titles in an elegant serif (Cormorant / Newsreader feel). Faint paper grain, softly imperfect color registration, intentional whitespace, scientific accuracy. NO modern logos, NO watermarks, NO glamour filters. Spelling must be exact.`;

export const PHOTOGRAPH_RECIPE =
  `A 1930s cosmetology-academy photograph that was shot in BLACK AND WHITE and DIGITALLY COLORIZED afterward. The tonal base is a genuine silver-gelatin grayscale photo with fine film grain and soft cinematic lighting; color is added in a digital colorization pass — muted, slightly-unnatural, gently desaturated tints painted over the grayscale so the black-and-white luminance still shows through. Restrained, believable color pulled toward the brand palette: moss-green (#5E6B55) and clay (#C9A58E) with parchment (#F5EFE6) neutrals and dusty-blue (#6C89AB) accents; vintage brass and marble where a setting appears. Shallow depth of field, magazine-quality composition. NO oversaturation, NO glamour filters, NO neon, NO modern logos or watermarks, NO "clean girl" influencer aesthetic.`;

export const CLOSEUP_RECIPE =
  `A 1930s cosmetology-academy textbook reference tile on aged parchment paper (#F5EFE6) with espresso-ink hairline borders, the condition name as an UPPERCASE serif title (Cormorant / Newsreader feel), UPPERCASE monospace labels on thin hairline leader lines, and a small serif caption strip at the bottom — but the central specimen window holds a REALISTIC PHOTOGRAPH, not an illustration: a true-to-life photo of the actual condition, mounted like an archival specimen print with subtle silver-gelatin grain and gently desaturated tones pulled toward the brand palette. The condition must be immediately recognizable the way it looks in real life — realistic, never stylized or cartooned — while staying calm and educational: tight crop on the affected area only, neutral presentation, no gore, no shock framing, no full identifiable faces. The ink leader lines and labels are drawn over and around the photo window so the vintage illustration frame still reads. NO modern logos, NO watermarks. Spelling must be exact.`;

export const RECIPES: Record<IllustrationKind, string> = {
  DRAWN: DRAWN_RECIPE,
  PHOTOGRAPH: PHOTOGRAPH_RECIPE,
  CLOSEUP: CLOSEUP_RECIPE,
};

export const KIND_LABEL: Record<IllustrationKind, string> = {
  DRAWN: 'Drawn illustration',
  PHOTOGRAPH: 'Photograph',
  CLOSEUP: 'Close-up reference tile',
};

// ---- kind classifier (advisory — every prompt states its own medium) ----
const DRAWN_STRONG =
  /\b(diagram|cross-?section|schematic|linework|line-?art|anatomical|infographic|illustration|drawn|graphic|colou?r wheel|level ladder|ladder|leader line|labeled|periodic|molecul|ph scale|1930s|textbook|scientific|sepia|parchment|espresso ink|ink line|table titled|chart|flow ?(?:chart| titled)?|timeline|funnel|six-pillar|mind map|game board|recipe card|numbered .*strip|sort it|split-panel|two-panel|side-by-side (?:bar|table)|cartoon|vignette|map-style|map of the)\b/i;
const CLOSEUP_PHOTO =
  /\b(clinic[a-z]+ (photo|close-?up)|dermatolog|real photo of (a )?(scalp|skin|lice|nit|rash|lesion)|photograph of (a )?(scalp|skin|lice|nit|rash|lesion|inflammation)|macro photo of (a )?scalp)\b/i;
const PHOTO =
  /\b(editor[a-z]+ (photo|shot|closeup|close-?up)|photograph|photo(?! ?graphic memory)|portrait|cinematic|still-?life|flat-?lay|macro (shot|photo)|lineup|top-down photo|photo grid|blow-?dr|magazine-quality)\b/i;
const PHOTO_SCENE =
  /\b(mentor|apprentice|salon|studio|dispensary|apothecary|hands (?:section|part|plac|hold|gently|working)|hair (?:cascad|being brush|held up)|model in a chair|client in the chair|proper ppe|shelf lined|arrangement of)\b/i;

export function classifyKind(prompt: string): IllustrationKind {
  const p = prompt.replace(/^[A-Za-z0-9_-]+\.(jpeg|jpg|png)\s*[—-]+\s*/, '');
  if (DRAWN_STRONG.test(p)) return 'DRAWN';
  if (CLOSEUP_PHOTO.test(p)) return 'CLOSEUP';
  if (PHOTO.test(p) || PHOTO_SCENE.test(p)) return 'PHOTOGRAPH';
  return 'DRAWN';
}

// ---- purge of the retired style-track words at compose time ----
const ART_WORD_BEFORE =
  /(reference|comparison|textbook|anatomical|anatomy|comparative-anatomy|scientific|scientific-textbook|safety|hygiene|timeline|acrostic|decision-tree|decision|directory|style|engraved|engraving|vintage|1930s|four-up|two-up|three-up|illustration|diagram|chart|map|colorized|drawn|line-art|"[^"]{1,40}")\s+$/i;

export function neutralize(text: string): string {
  let out = text.replace(/\bplat(?:e|es)\b/gi, (m: string, offset: number, whole: string) => {
    if (!ART_WORD_BEFORE.test(whole.slice(0, offset))) return m;
    const ill = /s$/i.test(m) ? 'illustrations' : 'illustration';
    return m[0] === m[0].toUpperCase() ? ill[0].toUpperCase() + ill.slice(1) : ill;
  });
  out = out.replace(/\bnon-?clinic[a-z]+\b/gi, 'warm');
  out = out.replace(/\bnot clinic[a-z]+\b/gi, 'not cold');
  out = out.replace(/\bclinic[a-z]+\s+(?=photo|close|reference|shot|image|style)/gi, 'close-up ');
  out = out.replace(/\bclinic[a-z]+ accurate\b/gi, 'true-to-life');
  out = out.replace(/\beditor[a-z]+\s+(?=photo|shot|close|still|portrait|lighting|luxury|looks?|work|style)/gi, '');
  out = out.replace(/\beditor[a-z]+\b/gi, 'photographic');
  return out;
}

/** Split an image block's `content` ("file.jpeg — caption") into its parts. */
export function splitImageContent(content: string): { filename: string | null; caption: string } {
  const m = content.match(/^([A-Za-z0-9_-]+\.(?:jpeg|jpg|png))\s*[—-]+\s*(.*)$/s);
  if (!m) return { filename: null, caption: content.trim() };
  return { filename: m[1], caption: m[2].trim() };
}

/**
 * The prompt for one illustration: the house recipe first (the same text the
 * work order prints), then the subject, then what must and must not be in it.
 */
export function composePrompt(input: {
  filename: string;
  kind: IllustrationKind;
  shows: string;
  labels: string;
  mustNot: string;
}): string {
  const lines: string[] = [];
  lines.push(RECIPES[input.kind]);
  lines.push('');
  lines.push(`Subject: ${neutralize(input.shows.trim()) || '(describe what the picture shows)'}`);
  const labels = input.labels.trim();
  if (labels) lines.push(`Labels that must appear, spelled exactly as written: ${labels}`);
  const mustNot = input.mustNot.trim();
  if (mustNot) lines.push(`Must not include: ${mustNot}`);
  lines.push('');
  lines.push(`One landscape image, at least 1600 px on the long side. File name: ${input.filename}`);
  return lines.join('\n');
}
